/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { next } from '@ember/runloop';
import { prettyJSONStringify } from 'cio/utils/objects';

interface SendEventModalSimpleEventDataEditorArgs {
  data: string;
  onChange: (content: string) => unknown;
}

type EventDataField = { key: string; value: string };

const EMPTY_EVENT_DATA_FIELD = Object.freeze({ key: '', value: '' });

function parse(data: string) {
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
}

function toJson(fields: EventDataField[]) {
  let attrs: Record<string, string> = {};

  fields
    .filter((field) => !isEmpty(field.key))
    .forEach((field) => {
      let attr = field.key.trim();
      /* Link issue - https://github.com/customerio/issues/issues/8055
        The JSON.stringify(field.value) is necessary to preserve and escape characters
        like quotes when sending events, to correctly convert deliveries against it's goals
       */
      let value = typeof field.value === 'string' ? JSON.stringify(field.value)?.trim() : field.value;

      try {
        attrs[attr] = JSON.parse(value);
      } catch {
        attrs[attr] = value;
      }
    });

  return prettyJSONStringify(attrs);
}

export default class SendEventModalSimpleEventDataEditor extends Component<SendEventModalSimpleEventDataEditorArgs> {
  @tracked fields: EventDataField[] = [];

  constructor(owner: unknown, args: SendEventModalSimpleEventDataEditorArgs) {
    super(owner, args);
    this.fields = this.generateFields(this.args.data);
  }

  @action
  addEventDataField() {
    this.fields = [...this.fields, { ...EMPTY_EVENT_DATA_FIELD }];
    this.args.onChange(toJson(this.fields));
    next(this, this.focusLastField);
  }

  @action
  removeEventDataField(idx: number) {
    if (this.fields.length <= 1) {
      return;
    }

    this.fields = this.fields.filter((_, i: number) => idx !== i);
    this.args.onChange(toJson(this.fields));
  }

  @action
  onInput(fieldIdx: number, key: string, value: string) {
    this.fields = this.fields.map((field, idx) => {
      if (idx === fieldIdx) {
        return {
          ...field,
          [key]: value,
        };
      }

      return field;
    });
    this.args.onChange(toJson(this.fields));
  }

  private generateFields(data: string) {
    let allowedTypesOfValue = ['string', 'number'];
    let fields = Object.entries(parse(data)).map(([key, value]: [string, string]) => {
      if (!allowedTypesOfValue.includes(typeof value)) {
        value = JSON.stringify(value);
      }
      return { key, value };
    });

    if (isEmpty(fields)) {
      fields.push({ ...EMPTY_EVENT_DATA_FIELD });
    }

    return fields;
  }

  private focusLastField() {
    let keyEls = document.querySelectorAll('.js-event-key input') as NodeListOf<HTMLInputElement>;
    keyEls[keyEls.length - 1]?.focus();
  }
}
